import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  InputBase,
  ButtonBase,
  MenuItem,
  Select,
  Link,
} from "@mui/material";
import logo from "../assets/legacy grey.png";
import { base_url } from "../Global";

// ── Roles (inline so no dependency on adminConfig during setup) ───────────────
const SELECTABLE_ROLES = [
  {
    value: "CONTENT_MOD",
    label: "Content Moderator",
    color: "#4ECDC4",
    description: "Reviews and removes user-generated content. Can issue account suspensions.",
  },
  {
    value: "SUBSCRIPTION_MOD",
    label: "Subscription Moderator",
    color: "#45B7D1",
    description: "Manages billing, subscription tiers and refunds.",
  },
  {
    value: "SUPPORT_STAFF",
    label: "Support Staff",
    color: "#6C63FF",
    description: "Manages support tickets and account recovery. No financial access.",
  },
  {
    value: "ANALYST",
    label: "Analyst",
    color: "#F7DC6F",
    description: "Read-only access to analytics, billing reports and user data.",
  },
  {
    value: "CAMPAIGN_BUILDER",
    label: "Campaign Builder",
    color: "#82E0AA",
    description: "Builds and dispatches push notifications and email campaigns.",
  },
  {
    value: "READ_ONLY_ADMIN",
    label: "Read Only Admin",
    color: "#95A5A6",
    description: "View-only access to high-level dashboard statistics.",
  },
];

const Register = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "READ_ONLY_ADMIN",
    inviteCode: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(base_url + "/auth/registerAdmin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
          inviteCode: form.inviteCode,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Registration failed.");

      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedRole = SELECTABLE_ROLES.find((r) => r.value === form.role);

  // ── Success screen ────────────────────────────────────────────────────────
  if (success) {
    return (
      <Box className="w-full relative px-[20px] pt-[50px] pb-[50px] h-[100vh] text-left text-[1.125rem] text-white font-gilroy bg-gray-400 shadow-[0px_4px_74px_rgba(0,_0,_0,_0.25)] overflow-hidden flex flex-col items-center justify-center box-border">
        <Box className="m-0 rounded-3xl bg-gray-200 flex flex-col items-center justify-center pt-[2rem] pb-[2rem] px-[3rem] gap-[1rem] z-[2] text-center">
          <Box
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: "#16a34a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "28px",
            }}
          >
            ✓
          </Box>
          <Typography
            component="h2"
            className="m-0 text-inherit text-[1.5rem] font-bold font-inherit capitalize"
          >
            Registration Submitted!
          </Typography>
          <Typography className="text-[0.9rem] text-gray-300 font-gilroy max-w-[300px]">
            Your account is pending approval by a Super Admin. You'll be redirected to login shortly.
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── Register form ─────────────────────────────────────────────────────────
  return (
    <Box className="w-full relative px-[20px] pt-[30px] pb-[30px] min-h-[100vh] text-left text-[1.125rem] text-white font-gilroy bg-gray-400 shadow-[0px_4px_74px_rgba(0,_0,_0,_0.25)] overflow-auto flex flex-col items-center justify-start box-border gap-[1rem] tracking-[normal]">
      <Box
        component="form"
        onSubmit={handleSubmit}
        className="m-0 rounded-3xl bg-gray-200 flex flex-col items-center justify-start mt-6 pt-[1.75rem] pb-[2rem] pr-[2.94rem] pl-[3rem] box-border gap-[0.85rem] w-full max-w-[480px] z-[2] mq700:pl-[1.5rem] mq700:pr-[1.44rem] mq700:box-border"
      >
        {/* Logo */}
        <Box className="self-stretch flex flex-col items-center justify-start gap-[0.5rem]">
          <Box
            component="img"
            className="w-[150px] h-[36px] relative z-[3]"
            loading="eager"
            alt="Logo"
            src={logo}
          />

          <Typography
            component="h1"
            className="m-0 flex-1 relative text-inherit leading-[2.375rem] capitalize font-bold font-inherit flex items-center z-[1]"
          >
            Create Admin Account
          </Typography>

          <Typography className="text-[0.85rem] text-gray-300 font-gilroy text-center -mt-2">
            Request access · Pending Super Admin approval
          </Typography>
        </Box>

        {/* Full Name */}
        <Field label="Full Name">
          <InputBase
            value={form.name}
            onChange={handleChange("name")}
            required
            placeholder="Your full name"
            className="w-full bg-gray-500 h-[48px] rounded-3xs py-[13px] px-[18px] font-gilroy font-light text-[1rem] text-gray-400 min-w-[300px] z-[1]"
          />
        </Field>

        {/* Email */}
        <Field label="Email Address">
          <InputBase
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            required
            placeholder="admin@example.com"
            className="w-full bg-gray-500 h-[48px] rounded-3xs py-[13px] px-[18px] font-gilroy font-light text-[1rem] text-gray-400 min-w-[300px] z-[1]"
          />
        </Field>

        {/* Password */}
        <Field label="Password">
          <InputBase
            type="password"
            value={form.password}
            onChange={handleChange("password")}
            required
            placeholder="Min. 8 characters"
            className="w-full bg-gray-500 h-[48px] rounded-3xs py-[13px] px-[18px] font-gilroy font-light text-[1rem] text-gray-800 min-w-[300px] z-[1]"
          />
        </Field>

        {/* Confirm Password */}
        <Field label="Confirm Password">
          <InputBase
            type="password"
            value={form.confirmPassword}
            onChange={handleChange("confirmPassword")}
            required
            placeholder="Repeat your password"
            className="w-full bg-gray-500 h-[48px] rounded-3xs py-[13px] px-[18px] font-gilroy font-light text-[1rem] text-gray-800 min-w-[300px] z-[1]"
          />
        </Field>

        {/* Role Selector */}
        <Field label="Requested Role">
          <Select
            value={form.role}
            onChange={handleChange("role")}
            variant="standard"
            disableUnderline
            className="w-full bg-gray-500 h-[48px] rounded-3xs px-[18px] font-gilroy font-light text-[1rem] min-w-[300px] z-[1]"
            sx={{
              color: selectedRole?.color || "#aaa",
              fontFamily: "Gilroy, sans-serif",
              "& .MuiSelect-icon": { color: "#aaa" },
            }}
          >
            {SELECTABLE_ROLES.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                <Box style={{ display: "flex", flexDirection: "column", gap: "2px", padding: "4px 0" }}>
                  <span style={{ color: role.color, fontWeight: 600, fontSize: "14px" }}>
                    {role.label}
                  </span>
                  <span style={{ color: "#9ca3af", fontSize: "11px" }}>
                    {role.description}
                  </span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Field>

        {/* Role preview */}
        {selectedRole && (
          <Box
            style={{
              width: "100%",
              borderRadius: "8px",
              padding: "10px 14px",
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Box
              style={{
                width: "10px", height: "10px", borderRadius: "50%",
                background: selectedRole.color, flexShrink: 0, marginTop: "4px",
              }}
            />
            <Typography style={{ color: "#d1d5db", fontSize: "12px", fontFamily: "Gilroy, sans-serif", lineHeight: 1.5 }}>
              <b style={{ color: selectedRole.color }}>{selectedRole.label}:</b>{" "}
              {selectedRole.description}
            </Typography>
          </Box>
        )}

        {/* Invite Code */}
        <Field label="Invite Code (optional)">
          <InputBase
            value={form.inviteCode}
            onChange={handleChange("inviteCode")}
            placeholder="Enter invite code if provided"
            className="w-full bg-gray-500 h-[48px] rounded-3xs py-[13px] px-[18px] font-gilroy font-light text-[1rem] text-gray-400 min-w-[300px] z-[1]"
          />
        </Field>

        {/* Error message */}
        {error && (
          <Box
            style={{
              width: "100%",
              borderRadius: "8px",
              background: "rgba(239,68,68,0.12)",
              border: "1px solid rgba(239,68,68,0.3)",
              padding: "10px 14px",
            }}
          >
            <Typography style={{ color: "#f87171", fontSize: "13px", fontFamily: "Gilroy, sans-serif" }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Submit button */}
        <Box className="self-stretch flex flex-col items-start justify-start max-w-full mt-1">
          <ButtonBase
            type="submit"
            disabled={loading}
            className="no-underline cursor-pointer bg-white self-stretch rounded-lg flex flex-row items-center justify-center box-border max-w-full whitespace-nowrap z-[3] hover:bg-gray-100"
            style={{ height: "48px", opacity: loading ? 0.7 : 1 }}
          >
            <Box className="relative text-[18px] leading-[40px] text-gray-200 capitalize font-semibold font-poppins text-left z-[4]">
              {loading ? "Submitting..." : "Request Access"}
            </Box>
          </ButtonBase>

          {/* Back to login */}
          <Box className="self-stretch flex flex-col items-center justify-start mt-3 z-[3]">
            <Link
              component="button"
              type="button"
              onClick={() => navigate("/login")}
              className="text-white text-[0.9rem] font-gilroy underline cursor-pointer"
            >
              Already have an account? Sign In
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Reusable field wrapper ───────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <Box className="self-stretch flex flex-col items-start justify-start gap-[0.25rem] w-full">
    <Box className="relative leading-[18px] capitalize flex items-center shrink-0 z-[1] text-[0.9rem]">
      {label}
    </Box>
    {children}
  </Box>
);

export default Register;